export type TemplateSyncStartedReason = 'publish' | 'memberValues';

export type TemplateSyncStartedEvent = {
  workspaceId: string;
  templateId: string;
  reason: TemplateSyncStartedReason;
  timestamp: number;
};

const TEMPLATE_SYNC_STARTED_EVENT = 'up2promo:template-sync-started';
const TEMPLATE_SYNC_STARTED_STORAGE_KEY = 'up2promo:template-sync-started';

function safeParseSyncEvent(
  raw: string | null,
): TemplateSyncStartedEvent | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as Partial<TemplateSyncStartedEvent>;
    if (
      typeof parsed.workspaceId !== 'string' ||
      typeof parsed.templateId !== 'string' ||
      (parsed.reason !== 'publish' && parsed.reason !== 'memberValues') ||
      typeof parsed.timestamp !== 'number'
    ) {
      return undefined;
    }
    return {
      workspaceId: parsed.workspaceId,
      templateId: parsed.templateId,
      reason: parsed.reason,
      timestamp: parsed.timestamp,
    };
  } catch {
    return undefined;
  }
}

export function emitTemplateSyncStarted(params: {
  workspaceId: string;
  templateId: string;
  reason: TemplateSyncStartedReason;
}): void {
  if (typeof window === 'undefined') return;
  const payload: TemplateSyncStartedEvent = {
    workspaceId: params.workspaceId,
    templateId: params.templateId,
    reason: params.reason,
    timestamp: Date.now(),
  };
  window.dispatchEvent(
    new CustomEvent<TemplateSyncStartedEvent>(TEMPLATE_SYNC_STARTED_EVENT, {
      detail: payload,
    }),
  );
  try {
    window.localStorage.setItem(
      TEMPLATE_SYNC_STARTED_STORAGE_KEY,
      JSON.stringify(payload),
    );
  } catch {
    // Ignore storage errors (private mode, quota, etc.).
  }
}

export function getLatestTemplateSyncStartedEvent():
  | TemplateSyncStartedEvent
  | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return safeParseSyncEvent(
      window.localStorage.getItem(TEMPLATE_SYNC_STARTED_STORAGE_KEY),
    );
  } catch {
    return undefined;
  }
}

export function subscribeTemplateSyncStarted(
  onEvent: (event: TemplateSyncStartedEvent) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const handleWindowEvent = (event: Event) => {
    const customEvent = event as CustomEvent<TemplateSyncStartedEvent>;
    if (customEvent.detail) {
      onEvent(customEvent.detail);
    }
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== TEMPLATE_SYNC_STARTED_STORAGE_KEY) return;
    const parsed = safeParseSyncEvent(event.newValue);
    if (parsed) {
      onEvent(parsed);
    }
  };

  window.addEventListener(
    TEMPLATE_SYNC_STARTED_EVENT,
    handleWindowEvent as EventListener,
  );
  window.addEventListener('storage', handleStorage);

  return () => {
    window.removeEventListener(
      TEMPLATE_SYNC_STARTED_EVENT,
      handleWindowEvent as EventListener,
    );
    window.removeEventListener('storage', handleStorage);
  };
}
